/**
 * Shared formatting utilities for analytics components
 * These are plain functions, not Vue composables
 */

/**
 * Format a number with compact notation (K, M, B, T)
 */
export function formatCompact(value) {
    if (!value) return '0';
    if (value >= 1e12) return (value / 1e12).toFixed(2) + 'T';
    if (value >= 1e9) return (value / 1e9).toFixed(2) + 'B';
    if (value >= 1e6) return (value / 1e6).toFixed(2) + 'M';
    if (value >= 1e3) return (value / 1e3).toFixed(1) + 'K';
    return value.toLocaleString();
}

/**
 * Format compute units with appropriate suffix
 */
export function formatCompute(value) {
    if (!value) return '0';
    if (value >= 1e12) return (value / 1e12).toFixed(1) + 'T';
    if (value >= 1e9) return (value / 1e9).toFixed(1) + 'B';
    return formatCompact(value);
}

/**
 * Format SOL values
 */
export function formatSOL(value, options = {}) {
    const { compact = true, decimals = 2 } = options;
    if (!value) return '0 SOL';

    if (compact && value >= 1000) {
        return (value / 1000).toFixed(1) + 'K SOL';
    }
    return value.toFixed(decimals) + ' SOL';
}

/**
 * Format USD values
 */
export function formatUSD(value, options = {}) {
    const { compact = true, decimals = 2 } = options;
    if (!value) return '$0';

    if (compact) {
        if (value >= 1e12) return '$' + (value / 1e12).toFixed(2) + 'T';
        if (value >= 1e9) return '$' + (value / 1e9).toFixed(2) + 'B';
        if (value >= 1e6) return '$' + (value / 1e6).toFixed(2) + 'M';
        if (value >= 1e3) return '$' + (value / 1e3).toFixed(1) + 'K';
    }
    return '$' + value.toFixed(decimals);
}

/**
 * Format a public key with ellipsis (e.g., "Token...5DA")
 */
export function formatPubkey(address, startChars = 4, endChars = 4) {
    if (!address || address.length < startChars + endChars + 2) return address;
    return `${address.slice(0, startChars)}..${address.slice(-endChars)}`;
}

/**
 * Get display label for a program - returns name if available, otherwise truncated pubkey.
 * Accepts either a program object (with .program and .name) or a raw address string.
 */
export function programLabel(programOrAddress, name) {
    if (typeof programOrAddress === 'object' && programOrAddress !== null) {
        return programOrAddress.name || formatPubkey(programOrAddress.program);
    }
    return name || formatPubkey(programOrAddress);
}

/**
 * Format percentage with optional color class
 */
export function formatPercent(value, options = {}) {
    const { decimals = 1, withSign = false } = options;
    if (value === null || value === undefined) return '-';

    const formatted = value.toFixed(decimals) + '%';
    if (withSign && value > 0) return '+' + formatted;
    return formatted;
}

/**
 * Get CSS class for efficiency values
 */
export function getEfficiencyClass(efficiency) {
    if (efficiency > 0.7) return 'text-success';
    if (efficiency > 0.4) return 'text-warning';
    return 'text-danger';
}

/**
 * Get color for efficiency values
 */
export function getEfficiencyColor(efficiency) {
    if (efficiency > 0.7) return '#22c55e';
    if (efficiency > 0.4) return '#f59e0b';
    return '#ef4444';
}

/**
 * Get class for reverted transaction percentage
 */
export function getRevertedClass(percent) {
    if (percent < 1) return 'text-success';
    if (percent < 5) return 'text-warning';
    return 'text-danger';
}

/**
 * Get class for Jito adoption percentage
 */
export function getJitoClass(percent) {
    if (percent > 50) return 'text-gradient';
    if (percent > 20) return 'text-info';
    return 'text-muted';
}

/**
 * Vue composable wrapper for backwards compatibility
 */
export function useFormatters() {
    return {
        formatCompact,
        formatCompute,
        formatSOL,
        formatUSD,
        formatPubkey,
        formatPercent,
        getEfficiencyClass,
        getEfficiencyColor,
        getRevertedClass,
        getJitoClass
    };
}

export default useFormatters;
